import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { PDFViewer } from '@/components/ui/PDFViewer';

export default function PdfViewerPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const bucket = params.get('bucket') || 'ticket-files';
  const path = params.get('path');

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const title = useMemo(() => {
    if (!path) return 'PDF Viewer';
    const filename = path.split('/').pop() || 'PDF';
    return `PDF Viewer — ${filename}`;
  }, [path]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    let objectUrl: string | null = null;

    const run = async () => {
      if (!path) {
        setPdfUrl(null);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase.storage.from(bucket).download(path);
        if (error) throw error;

        objectUrl = URL.createObjectURL(data);
        setPdfUrl(objectUrl);
      } catch (e) {
        console.error('Error downloading PDF:', e);
        setPdfUrl(null);
      } finally {
        setLoading(false);
      }
    };

    run();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [bucket, path]);

  return (
    <main className="min-h-[100dvh] bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-sm font-semibold truncate">{title}</h1>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : pdfUrl ? (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <PDFViewer url={pdfUrl} className="min-h-[80dvh]" />
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg p-10 text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3" />
            <p className="text-sm">Could not load PDF.</p>
          </div>
        )}
      </section>
    </main>
  );
}
